'use client';

import {
  forwardRef,
  type ComponentPropsWithoutRef,
  useId,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Menu as MenuPrimitive } from '@base-ui/react';
import { cn } from '../../../lib/utils';
import { Button, type ButtonProps } from '../../button/button';
import { Icon, type SvgIcon } from '../../icon/icon';
import { CheckboxIndicator } from '../../checkbox/checkbox/checkbox-indicator';
import { RadioIndicator } from '../../radio/radio/radio-indicator';
import {
  menuItemVariants,
  menuHeadingVariants,
  menuDividerVariants,
  MENU_ICON_SIZE,
  type MenuItemVariantsProps,
} from '../shared/menu-item-variants';
import {
  MenuDescriptionItemText,
  menuDescriptionItemVariants,
  type MenuDescriptionItemTextProps,
} from '../shared/menu-description-item';
import { useMenuSize } from '../shared/menu-size-context';
import { usePopoverMenuRootContext } from './popover-menu-root-context';

export const popoverMenuItemVariants = menuItemVariants;

export type PopoverMenuItemVariantsProps = MenuItemVariantsProps;

export type PopoverMenuItemProps = Omit<MenuPrimitive.Item.Props, 'className' | 'children'> & {
  children?: ReactNode;
  className?: string;
  icon?: SvgIcon;
  variant?: PopoverMenuItemVariantsProps['variant'];
};

export const PopoverMenuItem = forwardRef<HTMLElement, PopoverMenuItemProps>(
  ({ className, children, icon, variant = 'default', ...props }, ref) => {
    const { size } = useMenuSize();

    return (
      <MenuPrimitive.Item
        ref={ref}
        data-slot="popover-menu-item"
        className={cn(menuItemVariants({ size, variant }), className)}
        {...props}>
        {icon ? <Icon icon={icon} size={MENU_ICON_SIZE[size]} /> : null}
        {children}
      </MenuPrimitive.Item>
    );
  },
);
PopoverMenuItem.displayName = 'PopoverMenuItem';

export type PopoverMenuDescriptionItemProps = Omit<
  PopoverMenuItemProps,
  'icon' | 'title' | 'children'
> &
  Pick<MenuDescriptionItemTextProps, 'title' | 'description' | 'classNames' | 'children'>;

/**
 * Two-line row: a title with a description under it, clamped to two lines, plus
 * optional content below such as a Tag. No icon or chevron.
 */
export const PopoverMenuDescriptionItem = forwardRef<HTMLElement, PopoverMenuDescriptionItemProps>(
  ({ className, title, description, classNames, children, ...props }, ref) => {
    const { size } = useMenuSize();
    const descriptionId = useId();

    return (
      <PopoverMenuItem
        ref={ref}
        data-slot="popover-menu-description-item"
        aria-describedby={description ? descriptionId : undefined}
        className={cn(menuDescriptionItemVariants({ size }), className)}
        {...props}>
        <MenuDescriptionItemText
          size={size}
          title={title}
          description={description}
          descriptionId={descriptionId}
          classNames={classNames}>
          {children}
        </MenuDescriptionItemText>
      </PopoverMenuItem>
    );
  },
);
PopoverMenuDescriptionItem.displayName = 'PopoverMenuDescriptionItem';

export type PopoverMenuCheckboxItemProps = Omit<
  MenuPrimitive.CheckboxItem.Props,
  'className' | 'children'
> & {
  children?: ReactNode;
  className?: string;
};

export const PopoverMenuCheckboxItem = forwardRef<HTMLElement, PopoverMenuCheckboxItemProps>(
  ({ className, children, closeOnClick = false, onClick, ...props }, ref) => {
    const { size } = useMenuSize();
    const { skipExitAnimation } = usePopoverMenuRootContext();

    const handleClick: NonNullable<PopoverMenuCheckboxItemProps['onClick']> = (event) => {
      onClick?.(event);

      if (closeOnClick) {
        skipExitAnimation();
      }
    };

    return (
      <MenuPrimitive.CheckboxItem
        ref={ref}
        data-slot="popover-menu-checkbox-item"
        className={cn(menuItemVariants({ size }), className)}
        closeOnClick={closeOnClick}
        onClick={handleClick}
        // the unchecked box must stay visible, so the indicator reads state from the render callback
        render={(itemProps, state) => (
          <div {...itemProps}>
            <CheckboxIndicator checked={state.checked} disabled={state.disabled} size={size} />
            {children}
          </div>
        )}
        {...props}
      />
    );
  },
);
PopoverMenuCheckboxItem.displayName = 'PopoverMenuCheckboxItem';

export type PopoverMenuRadioGroupProps = Omit<MenuPrimitive.RadioGroup.Props, 'className'> & {
  className?: string;
};

export const PopoverMenuRadioGroup = ({ className, ...props }: PopoverMenuRadioGroupProps) => (
  <MenuPrimitive.RadioGroup
    data-slot="popover-menu-radio-group"
    className={cn('flex flex-col gap-0.5', className)}
    {...props}
  />
);

export type PopoverMenuRadioItemProps = Omit<
  MenuPrimitive.RadioItem.Props,
  'className' | 'children'
> & {
  children?: ReactNode;
  className?: string;
};

export const PopoverMenuRadioItem = forwardRef<HTMLElement, PopoverMenuRadioItemProps>(
  ({ className, children, closeOnClick = false, onClick, ...props }, ref) => {
    const { size } = useMenuSize();
    const { skipExitAnimation } = usePopoverMenuRootContext();

    const handleClick: NonNullable<PopoverMenuRadioItemProps['onClick']> = (event) => {
      onClick?.(event);

      if (closeOnClick) {
        skipExitAnimation();
      }
    };

    return (
      <MenuPrimitive.RadioItem
        ref={ref}
        data-slot="popover-menu-radio-item"
        className={cn(menuItemVariants({ size }), className)}
        closeOnClick={closeOnClick}
        onClick={handleClick}
        render={(itemProps, state) => (
          <div {...itemProps}>
            <RadioIndicator checked={state.checked} disabled={state.disabled} size={size} />
            {children}
          </div>
        )}
        {...props}
      />
    );
  },
);
PopoverMenuRadioItem.displayName = 'PopoverMenuRadioItem';

export type PopoverMenuGroupProps = Omit<MenuPrimitive.Group.Props, 'className'> & {
  className?: string;
};

export const PopoverMenuGroup = ({ className, ...props }: PopoverMenuGroupProps) => (
  <MenuPrimitive.Group
    data-slot="popover-menu-group"
    className={cn('flex flex-col gap-0.5', className)}
    {...props}
  />
);

export type PopoverMenuHeadingProps = Omit<MenuPrimitive.GroupLabel.Props, 'className'> & {
  className?: string;
};

export const PopoverMenuHeading = forwardRef<HTMLDivElement, PopoverMenuHeadingProps>(
  ({ className, ...props }, ref) => {
    const { size } = useMenuSize();

    return (
      <MenuPrimitive.GroupLabel
        ref={ref}
        data-slot="popover-menu-heading"
        className={cn(menuHeadingVariants({ size }), className)}
        {...props}
      />
    );
  },
);
PopoverMenuHeading.displayName = 'PopoverMenuHeading';

export type PopoverMenuDividerProps = Omit<MenuPrimitive.Separator.Props, 'className'> & {
  className?: string;
};

export const PopoverMenuDivider = forwardRef<HTMLDivElement, PopoverMenuDividerProps>(
  ({ className, ...props }, ref) => {
    const { size } = useMenuSize();

    return (
      <MenuPrimitive.Separator
        ref={ref}
        data-slot="popover-menu-divider"
        className={cn(menuDividerVariants({ size }), className)}
        {...props}
      />
    );
  },
);
PopoverMenuDivider.displayName = 'PopoverMenuDivider';

export type PopoverMenuFooterProps = ComponentPropsWithoutRef<'div'>;

const FOOTER_ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"])';

export const PopoverMenuFooter = ({ className, onKeyDown, ...props }: PopoverMenuFooterProps) => {
  // Base UI only walks the popup vertically; Left/Right step between the footer buttons.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);

    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }

    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(FOOTER_ITEM_SELECTOR),
    );
    const current = items.indexOf(document.activeElement as HTMLElement);

    if (current === -1) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    items[current + (event.key === 'ArrowRight' ? 1 : -1)]?.focus();
  };

  return (
    <div
      role="group"
      data-slot="popover-menu-footer"
      className={cn('flex items-center justify-end gap-2 px-1 pt-2 pb-1', className)}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
};

export type PopoverMenuButtonProps = Omit<
  MenuPrimitive.Item.Props,
  'className' | 'render' | 'nativeButton'
> &
  Pick<ButtonProps, 'variant' | 'icon' | 'iconRight' | 'className'>;

export const PopoverMenuButton = forwardRef<HTMLButtonElement, PopoverMenuButtonProps>(
  (
    { className, children, variant = 'primary', icon, iconRight, closeOnClick = true, ...props },
    ref,
  ) => (
    <MenuPrimitive.Item
      ref={ref}
      data-slot="popover-menu-button"
      nativeButton
      closeOnClick={closeOnClick}
      render={
        <Button
          size="sm"
          variant={variant}
          icon={icon}
          iconRight={iconRight}
          className={className}
        />
      }
      {...props}>
      {children}
    </MenuPrimitive.Item>
  ),
);
PopoverMenuButton.displayName = 'PopoverMenuButton';
